// ─── fastn MCP server ─────────────────────────────────────────────────────────
// The hosted MCP endpoint an AI client (Claude, Claude Code, Claude Desktop)
// points at to reach this account's connectors and workflows as tools.

/** Key the server is registered under in a CLI / config-file client. */
export const MCP_SERVER_NAME = "fastn";

/** Display name shown in Claude's connector list. */
export const MCP_CONNECTOR_NAME = "Fastn";

/**
 * The data plane that serves MCP. `connect.<zone>` is the CONTROL plane and
 * answers `404 {"error":"not found - MCP is served on the mcp host"}`, which is
 * why the old `connect.fastn.dev/mcp` default could never work.
 */
export const MCP_DEFAULT_SERVER_URL = "https://mcp.fastn.dev";

/** UCL's reserved path that connects Fastn Workspace with no tool picker. */
export const FASTN_WORKSPACE_PATH = "/fastn-workspace";

/**
 * The hosted MCP endpoint.
 *
 * `VITE_MCP_SERVER_URL` wins when set; otherwise the default above. The default
 * exists so the dialog is usable in a deployment that has not set the variable
 * — previously it rendered nothing but "ask an administrator", which is a dead
 * end for the person reading it.
 *
 * NO `/mcp` SEGMENT. The default carried one, guessed rather than confirmed:
 * probing cannot settle it, because the data plane checks auth BEFORE routing,
 * so `POST /mcp`, `POST /fastn/prod-fastn-agent` (known good) and
 * `POST /nonsense-path` all answer an identical `401 {"error":"unauthorized"}`
 * and a valid path is indistinguishable from a typo without a credential.
 *
 * Two things settle it from inside the repo instead. The deploy overlays
 * describe the resource form as `mcp.<zone>/<org>[/<workspace>]` with "no
 * redundant /mcp segment"; and prod's own UCL_COMPAT_URL — the URL this repo
 * calls that host with — is the bare `https://mcp.fastn.dev`
 * (deploy/k8s/overlays/prod/kustomization.yaml). The guess was wrong.
 *
 * Still the HOST only, which is the honest default: the full resource form
 * takes an org (`.env.example` shows `https://mcp.fastn.dev/<org>`) and this
 * constant cannot know which. VITE_MCP_SERVER_URL stays the way to give a
 * deployment the per-org URL — per env, already wired through
 * Dockerfile.dashboard and pipeline.yaml — without a code change.
 */
export const MCP_SERVER_URL: string = withFastnWorkspace(
  normalizeMcpServerUrl(import.meta.env.VITE_MCP_SERVER_URL),
);

/**
 * Point a bare MCP host at Fastn Workspace.
 *
 * On UCL the bare `mcp.<zone>` shows the tool picker with Fastn Workspace
 * unticked; that is the URL for the Claude directory and UCL users. People
 * arriving from this dashboard are integration users, so they get
 * `mcp.<zone>/fastn-workspace`, which connects Fastn Workspace straight away.
 * A configured org or workspace path is the deployment's explicit choice and
 * is left alone.
 */
export function withFastnWorkspace(url: string): string {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return url;
  }
  return pathname.replace(/\/+$/, "") === "" ? url + FASTN_WORKSPACE_PATH : url;
}

/**
 * Bring a configured value onto the resource form: no trailing slash, and no
 * redundant `/mcp` segment.
 *
 * The constant above lost that segment, but the value that actually ships is
 * `VITE_MCP_SERVER_URL`, and that lives in GitHub repo variables — outside this
 * repo, where nothing here can see it. Both of them kept the segment
 * (`https://mcp.fastn.dev/mcp` prod, `https://mcp.fastn-test.dev/mcp` dev) long
 * after the default dropped it, so every built dashboard went on handing Claude
 * a URL that is not the documented form. Editing the variables fixes today;
 * this is what keeps it fixed.
 *
 * An org literally named `mcp` would be stripped too. None exists, and the
 * segment is far likelier to be this mistake than a real org.
 */
export function normalizeMcpServerUrl(raw: string | undefined): string {
  const configured = (raw ?? "")
    .trim()
    .replace(/\/+$/, "")
    .replace(/(?:\/mcp)+\/?$/i, "");
  return configured || MCP_DEFAULT_SERVER_URL;
}

/**
 * Claude's documented install link: opens the "Add custom connector" dialog on
 * claude.ai with the name and URL already filled in.
 *
 * Prefill only — Claude shows a notice that the values came from an external
 * link, and the user still confirms and authorizes before anything is added.
 * A signed-out user signs in first and lands back on the prefilled dialog.
 *
 * https://claude.com/docs/connectors/building/directory-vs-custom#share-an-install-link
 */
const CLAUDE_CUSTOMIZE_CONNECTORS = "https://claude.ai/customize/connectors";

/** Org-wide equivalent, for Team/Enterprise owners adding it for everyone. */
const CLAUDE_ADMIN_CONNECTORS = "https://claude.ai/admin-settings/connectors";

function installUrl(base: string, url: string, name: string): string {
  const params = new URLSearchParams({
    modal: "add-custom-connector",
    connectorName: name,
    connectorUrl: url,
  });
  return `${base}?${params.toString()}`;
}

/* The url is a required argument on every builder below: with MCP_SERVER_URL
   nullable, a default would have silently reintroduced an unusable value. */

/** Prefilled "Add custom connector" dialog for the signed-in user. */
export function claudeInstallUrl(url: string, name: string = MCP_CONNECTOR_NAME): string {
  return installUrl(CLAUDE_CUSTOMIZE_CONNECTORS, url, name);
}

/** Prefilled org-wide connector dialog. Team/Enterprise owners only. */
export function claudeAdminInstallUrl(url: string, name: string = MCP_CONNECTOR_NAME): string {
  return installUrl(CLAUDE_ADMIN_CONNECTORS, url, name);
}

/** One-liner for the Claude Code CLI. */
export function claudeCodeCommand(url: string): string {
  return `claude mcp add --transport http ${MCP_SERVER_NAME} ${url}`;
}

/**
 * Snippet for `claude_desktop_config.json`.
 *
 * Entries in that file are stdio launch specs (`command` + `args`) — it does
 * not take a remote HTTP endpoint directly, so a `{ type: "http", url }` entry
 * silently yields no server. `mcp-remote` is the standard stdio bridge to a
 * remote MCP server and handles the OAuth handshake.
 */
export function claudeDesktopConfig(url: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_NAME]: { command: "npx", args: ["-y", "mcp-remote", url] },
      },
    },
    null,
    2,
  );
}

/**
 * Clipboard write that reports failure instead of throwing.
 *
 * `navigator.clipboard` is undefined on insecure origins and rejects when the
 * document is not focused, so a bare `writeText()` leaves the user with a
 * "Copied" confirmation and an empty clipboard.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* ─── Other MCP clients ───────────────────────────────────────────────────────
   Everything below turns the one server URL above into whatever each client
   actually accepts. The formats are NOT interchangeable and the differences are
   not cosmetic — Antigravity rejects `url`, Cursor rejects
   `serverUrl`, VS Code keys the object `servers` rather than `mcpServers`, and
   Cline silently falls back to SSE (405) unless `type` is the camelCase
   `streamableHttp`. Each entry below is the shape that client's own docs
   document; a "close enough" snippet here is a support ticket there.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * UTF-8 safe base64, for Cursor's deeplink.
 *
 * `btoa` is latin1-only and throws on any code point above U+00FF. The server
 * URL is ASCII in every deployment we ship, but it is assembled from an org
 * name we do not control, so the encode is done over UTF-8 bytes rather than
 * trusting that.
 */
function base64(value: string): string {
  let binary = "";
  for (const byte of new TextEncoder().encode(value)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Pretty-printed JSON, the form every config file below is pasted as. */
function json(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

/**
 * Cursor's one-click install deeplink.
 *
 * `config` is base64 of the SERVER OBJECT ALONE — not of `{ [name]: {...} }`.
 * The name travels separately in its own query parameter, and double-wrapping
 * it produces a server whose transport Cursor cannot read.
 *
 * https://cursor.com/docs/mcp/install-links
 */
export function cursorInstallUrl(url: string, name: string = MCP_SERVER_NAME): string {
  const params = new URLSearchParams({ name, config: base64(JSON.stringify({ url })) });
  return `cursor://anysphere.cursor-deeplink/mcp/install?${params.toString()}`;
}

/** `~/.cursor/mcp.json` (global) or `.cursor/mcp.json` (per project). */
export function cursorConfig(url: string): string {
  return json({ mcpServers: { [MCP_SERVER_NAME]: { url } } });
}

/**
 * VS Code's install URL handler.
 *
 * The whole server object — name included — is a single JSON-stringified,
 * percent-encoded query STRING, not a set of key=value parameters, so it is
 * built by hand rather than through URLSearchParams (which would emit a
 * `?json=` style pair VS Code does not parse).
 *
 * https://code.visualstudio.com/api/extension-guides/ai/mcp
 */
export function vscodeInstallUrl(url: string, name: string = MCP_SERVER_NAME): string {
  const config = JSON.stringify({ name, type: "http", url });
  return `vscode:mcp/install?${encodeURIComponent(config)}`;
}

/** `.vscode/mcp.json`. Keyed `servers`, not `mcpServers` — VS Code's own shape. */
export function vscodeConfig(url: string): string {
  return json({ servers: { [MCP_SERVER_NAME]: { type: "http", url } } });
}

/** VS Code CLI equivalent of the install link. */
export function vscodeCommand(url: string): string {
  // The inner quotes are escaped because the argument is one shell-quoted JSON
  // string; copied as-is this runs in bash, zsh and PowerShell.
  const config = JSON.stringify({ name: MCP_SERVER_NAME, type: "http", url });
  return `code --add-mcp ${JSON.stringify(config)}`;
}

/** Codex CLI. The name is positional and precedes `--url`. */
export function codexCommand(url: string): string {
  return `codex mcp add ${MCP_SERVER_NAME} --url ${url}`;
}

/** `~/.codex/config.toml`. TOML, not JSON — the one config file here that is. */
export function codexConfig(url: string): string {
  return `[mcp_servers.${MCP_SERVER_NAME}]\nurl = "${url}"`;
}

/**
 * Devin CLI. `-s user` writes `~/.config/devin/mcp_config.json`, which Devin
 * Desktop reads too; `login` runs the OAuth flow the add alone does not.
 */
export function devinCommand(url: string): string {
  return `devin mcp add -s user ${MCP_SERVER_NAME} ${url} && devin mcp login ${MCP_SERVER_NAME}`;
}

/**
 * Antigravity's remote field is `serverUrl`, and its docs say plainly that
 * "legacy fields like `url` or `httpUrl` are not supported".
 */
export function serverUrlConfig(url: string): string {
  return json({ mcpServers: { [MCP_SERVER_NAME]: { serverUrl: url } } });
}

/**
 * Cline's `cline_mcp_settings.json`.
 *
 * `streamableHttp` is camelCase on purpose. `streamable-http` — or omitting
 * `type` — makes Cline fall back to SSE and the connection dies on a 405.
 */
export function clineConfig(url: string): string {
  return json({
    mcpServers: { [MCP_SERVER_NAME]: { type: "streamableHttp", url } },
  });
}

/* ─── Client registry ─────────────────────────────────────────────────────── */

/** Stable per-client key. Used for testids and for the analytics dimension. */
export type McpClientId =
  | "claude"
  | "claude-manual"
  | "cursor"
  | "vscode"
  | "codex"
  | "antigravity"
  | "devin"
  | "cline"
  | "chatgpt"
  | "lovable"
  | "bolt";

/** A one-click action that opens the client with the server prefilled. */
export interface McpClientLink {
  label: string;
  href: string;
}

/** Something the user copies: a shell command, or a config file's contents. */
export interface McpClientSnippet {
  /** Names the value in the copy button, its aria-label and the copy event. */
  label: string;
  /**
   * Micro-heading above the field.
   *
   * Only set where one client offers several snippets and they would otherwise
   * be indistinguishable: Claude carries both a Claude Code command and a
   * Claude Desktop config, and an unlabelled pair of boxes is a guess.
   */
  title?: string;
  value: string;
  /** Where it goes. Omitted for commands, which go in a shell. */
  path?: string;
  /** Multi-line values get a scrolling, wrap-preserving field. */
  multiline?: boolean;
}

export interface McpClient {
  id: McpClientId;
  name: string;
  /** Key into AGENT_MARKS. Several clients share one vendor's mark. */
  mark: string;
  /** One line on what the client is, so the list can be scanned. */
  blurb: string;
  install?: McpClientLink;
  /** Further one-click actions, below the primary one. */
  altLinks?: McpClientLink[];
  snippets: McpClientSnippet[];
  /**
   * Click-path for clients that are configured only through their own UI and
   * have no file to edit and no link to open. The copyable server-URL field at
   * the foot of the panel is the value these steps refer to.
   */
  steps?: string[];
  /** A caveat that would otherwise cost the user a failed attempt. */
  note?: string;
  /**
   * Shown expanded at the top, above the picker, instead of as a tile.
   *
   * Claude alone: it is the only client with a true one-click install, so its
   * whole setup is two sentences and a button. Expanding it costs the height
   * of one card and means the common case needs no click at all.
   */
  recommended?: boolean;
}

/**
 * Every client fastn can be added to, in the order the dialog lists them.
 *
 * Claude leads because it is the one with a true one-click install and the
 * surface most of our users arrive from; the rest are ordered by how much work
 * they take (link, then CLI, then a file to edit, then a UI to click through).
 *
 * Kept as a function rather than a constant so each snippet is built from the
 * URL the caller resolved, instead of a module-load-time read that a test or a
 * per-org override could not vary.
 */
export function mcpClients(url: string): McpClient[] {
  return [
    {
      id: "claude",
      recommended: true,
      mark: "anthropic",
      name: "Claude",
      blurb: "claude.ai, in the browser or the desktop app.",
      // Nothing but the button. This card sits above the server URL, so every
      // instruction that refers to that URL belongs below it, in the Claude
      // entry under Other. What is left here is the one path that needs no URL
      // at all: the link carries it.
      install: { label: "Add to Claude", href: claudeInstallUrl(url) },
      snippets: [],
    },
    {
      id: "claude-manual",
      mark: "anthropic",
      name: "Claude",
      blurb: "By hand, Claude Code, Claude Desktop, whole org.",
      steps: [
        "By hand: open Settings, then Connectors, then Add custom connector.",
        "Paste the MCP server URL below and authorise fastn.",
      ],
      note: "The one-click link above opens Claude with the connector filled in. You review it and authorise fastn yourself. Claude never adds a connector from a link on its own.",
      snippets: [
        {
          title: "Claude Code",
          label: "Claude Code command",
          value: claudeCodeCommand(url),
        },
        {
          title: "Claude Desktop",
          label: "Claude Desktop config",
          value: claudeDesktopConfig(url),
          path: "claude_desktop_config.json",
          multiline: true,
        },
      ],
      altLinks: [{ label: "Add for the whole organization", href: claudeAdminInstallUrl(url) }],
    },
    {
      id: "cursor",
      mark: "cursor",
      name: "Cursor",
      blurb: "The Cursor editor, global or per project.",
      install: { label: "Add to Cursor", href: cursorInstallUrl(url) },
      snippets: [
        {
          label: "Cursor config",
          value: cursorConfig(url),
          path: "~/.cursor/mcp.json",
          multiline: true,
        },
      ],
      note: "The button hands the server to Cursor's own install dialog. Use the file instead for a project-scoped server, at .cursor/mcp.json in the repo.",
    },
    {
      id: "vscode",
      mark: "vscode",
      name: "VS Code",
      blurb: "GitHub Copilot's agent mode in VS Code.",
      install: { label: "Install in VS Code", href: vscodeInstallUrl(url) },
      snippets: [
        { title: "Command line", label: "VS Code command", value: vscodeCommand(url) },
        {
          label: "VS Code config",
          value: vscodeConfig(url),
          path: ".vscode/mcp.json",
          multiline: true,
        },
      ],
      note: 'VS Code keys this object "servers", not "mcpServers". The shape the other editors here use will not load.',
    },
    {
      id: "codex",
      mark: "openai",
      name: "Codex",
      blurb: "OpenAI's Codex CLI and its VS Code extension.",
      snippets: [
        { title: "Command line", label: "Codex command", value: codexCommand(url) },
        {
          label: "Codex config",
          value: codexConfig(url),
          path: "~/.codex/config.toml",
          multiline: true,
        },
      ],
      note: "TOML, not JSON. The CLI and the VS Code extension read the same file, so adding it once covers both.",
    },
    {
      id: "antigravity",
      mark: "antigravity",
      name: "Antigravity",
      blurb: "Google's agentic IDE and its CLI.",
      snippets: [
        {
          label: "Antigravity config",
          value: serverUrlConfig(url),
          path: "~/.gemini/config/mcp_config.json",
          multiline: true,
        },
      ],
      note: 'Antigravity\'s remote field is "serverUrl". It does not accept "url" or "httpUrl". In the IDE you can reach this file from the agent panel: the menu at the top, then MCP Servers, then Manage MCP Servers, then View raw config. Use .agents/mcp_config.json for a workspace-only server.',
    },
    {
      id: "cline",
      mark: "cline",
      name: "Cline",
      blurb: "The Cline extension for VS Code.",
      snippets: [
        {
          label: "Cline config",
          value: clineConfig(url),
          path: "cline_mcp_settings.json",
          multiline: true,
        },
      ],
      note: 'Open it from the Cline panel: the menu at the top right, then MCP Servers, then Configure MCP Servers. The type must be the camelCase "streamableHttp"; anything else makes Cline fall back to SSE and fail with a 405.',
    },
    {
      id: "devin",
      mark: "devin",
      name: "Devin",
      blurb: "Devin Desktop, formerly Windsurf, and the Devin CLI.",
      steps: [
        "In Devin Desktop, open Customize in the sidebar, then the MCPs tab, then + and New MCP.",
        "Name it fastn, set Transport type to HTTP, paste the MCP server URL below, and set Authentication to OAuth.",
        "Click Add, tick I understand and want to proceed, then click Add again.",
        "Click Connect MCP, sign in to fastn in the browser and pick your workspace.",
      ],
      snippets: [{ title: "Devin CLI", label: "Devin CLI command", value: devinCommand(url) }],
      note: "fastn signs you in with OAuth, so there is no header or API key to add.",
    },
    {
      id: "chatgpt",
      mark: "openai",
      name: "ChatGPT",
      blurb: "Custom connectors, via developer mode.",
      snippets: [],
      steps: [
        "Open Settings, then Apps & Connectors, then Advanced settings, and turn on Developer mode.",
        "Back in Connectors, click Add custom connector.",
        "Give it a name, paste the MCP server URL below, and pick OAuth as the auth method.",
      ],
      note: "Developer mode is not on the free tier. It needs Plus, Pro, Business, Enterprise or Edu.",
    },
    {
      id: "lovable",
      mark: "lovable",
      name: "Lovable",
      blurb: "Chat connectors, in the Lovable builder.",
      snippets: [],
      steps: [
        "Open Connectors and go to the Chat connectors tab.",
        "Click New MCP server.",
        "Name it fastn, paste the MCP server URL below, leave auth on OAuth, and click Add & authorize.",
      ],
      note: "Chat connectors attach to your build session, not to the app you ship. This gives Lovable's chat your fastn tools while you iterate.",
    },
    {
      id: "bolt",
      mark: "bolt",
      name: "Bolt",
      blurb: "Custom MCP connectors, in bolt.new.",
      snippets: [],
      steps: [
        "Open the Connectors (MCP) page.",
        "Choose to add a custom MCP server.",
        "Paste the MCP server URL below and authorise fastn.",
      ],
    },
  ];
}
